import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ScanLine, Search } from 'lucide-react';
import apiClient from '../../api/client';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { useToast } from '../../components/ui/Toast';
import { formatDate, formatEUR } from '../../lib/format';
import { cn } from '../../utils/cn';
import {
  cmdHeaders,
  returnError,
  CONDITIONS,
  condLabel,
  Field,
  Panel,
  PortalReturnTabs,
  reasonLabel,
  returnsBase,
  tdCls,
  thCls,
  useReturnsRole,
  Verdict,
  type Preview,
  type PurchaseView,
} from '../../components/returns/returnsShared';
import { NoReceiptDialog } from './NoReceiptDialog';

/**
 * A new return — at the counter (staff) or from the garage portal (returns v2). Find the part the
 * customer bought (scan its EAN, type its reference, or start from a delivery document), tick it,
 * say why and in what state: the same rules as the server judge each line live (accepted / a
 * manager decides / impossible, with the amount and the legal note). The price, the date and the
 * delay come from the purchase, never from the screen.
 */

interface Customer {
  id: string;
  name: string;
  code: string | null;
  customer_type: string;
}
interface Sel {
  qty: number;
  reason: string;
  condition: string;
  warranty: { plate?: string; km?: string; mountedAt?: string; description?: string };
}
const VAT = (ht: number, rate: number) => Math.round(ht * (1 + rate / 100) * 100) / 100;

export default function ReturnNew() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { push, showApiError } = useToast();
  const { portal, viewer } = useReturnsRole();
  const base = returnsBase(portal);

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [custQ, setCustQ] = useState('');
  const [custHits, setCustHits] = useState<Customer[]>([]);
  const [docScan, setDocScan] = useState('');
  const [mode, setMode] = useState<'ART' | 'DOC'>('ART');
  const [q, setQ] = useState('');
  const [doc, setDoc] = useState('');
  const [docs, setDocs] = useState<
    Array<{ document: string; date: string | null; open: number; warranty: number }>
  >([]);
  const [found, setFound] = useState<{ eligible: PurchaseView[]; other: PurchaseView[] }>({
    eligible: [],
    other: [],
  });
  const [known, setKnown] = useState<Record<string, PurchaseView>>({});
  const [sel, setSel] = useState<Record<string, Sel>>({});
  const [preview, setPreview] = useState<Preview | null>(null);
  const [partPresent, setPartPresent] = useState(true);
  const [pickup, setPickup] = useState<'COUNTER' | 'DRIVER'>('COUNTER');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [noReceipt, setNoReceipt] = useState(false);
  const [policyDays, setPolicyDays] = useState<number | null>(null);
  const cmd = useRef<string>(crypto.randomUUID());

  useEffect(() => {
    apiClient
      .get<{ policy: { returnDays: number } }>('/returns/policy')
      .then((r) => setPolicyDays(r.data.policy.returnDays))
      .catch(() => undefined);
  }, []);

  // The garage portal is its own customer.
  useEffect(() => {
    if (!portal) return;
    setCustomer({ id: 'self', name: '', code: null, customer_type: 'B2B' });
  }, [portal]);
  const customerId = portal ? undefined : customer?.id;

  // Customer search (staff).
  useEffect(() => {
    if (portal || customer || custQ.trim().length < 2) {
      setCustHits([]);
      return;
    }
    const h = setTimeout(() => {
      apiClient
        .get<{ rows: Customer[] }>(`/customers?q=${encodeURIComponent(custQ.trim())}&limit=8`)
        .then((r) => setCustHits(r.data.rows))
        .catch(() => setCustHits([]));
    }, 200);
    return () => clearTimeout(h);
  }, [custQ, customer, portal]);

  const purchasesQs = useCallback(
    (extra: Record<string, string>) => {
      const qs = new URLSearchParams(extra);
      if (customerId) qs.set('customerId', customerId);
      return qs.toString();
    },
    [customerId]
  );

  // Purchases: by article (EAN / reference), or every line of a delivery document.
  useEffect(() => {
    if (!customer) return;
    const h = setTimeout(() => {
      if (mode === 'DOC' && !doc) {
        setFound({ eligible: [], other: [] });
        return;
      }
      const extra: Record<string, string> = mode === 'DOC' ? { document: doc } : { q };
      apiClient
        .get<{ eligible: PurchaseView[]; other: PurchaseView[] }>(
          `/returns/purchases?${purchasesQs(extra)}`
        )
        .then((r) => {
          setFound(r.data);
          setKnown((k) => ({
            ...k,
            ...Object.fromEntries(
              [...r.data.eligible, ...r.data.other].map((x) => [x.orderLineId, x])
            ),
          }));
        })
        .catch(showApiError);
    }, 200);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, mode, q, doc, purchasesQs]);

  useEffect(() => {
    if (!customer) return;
    apiClient
      .get<typeof docs>(`/returns/purchases/documents?${purchasesQs({})}`)
      .then((r) => setDocs(r.data))
      .catch(() => setDocs([]));
  }, [customer, purchasesQs]);

  const draftLines = useMemo(
    () =>
      Object.entries(sel).map(([orderLineId, s]) => ({
        orderLineId,
        qty: s.qty,
        reason: s.reason,
        condition: s.reason === 'CORE' ? 'MOUNTED' : s.condition,
        warranty: s.reason === 'WARRANTY' ? s.warranty : undefined,
      })),
    [sel]
  );

  // Live verdict: the server's own rules, on every change.
  useEffect(() => {
    if (!customer || !draftLines.length) {
      setPreview(null);
      return;
    }
    const h = setTimeout(() => {
      apiClient
        .post<Preview>('/returns/preview', {
          customerId: customerId ?? '00000000-0000-0000-0000-000000000000',
          lines: draftLines,
        })
        .then((r) => setPreview(r.data))
        .catch(() => setPreview(null));
    }, 250);
    return () => clearTimeout(h);
  }, [draftLines, customer, customerId]);

  const pick = (x: PurchaseView, on: boolean) => {
    setSel((cur) => {
      const next = { ...cur };
      if (!on) {
        delete next[x.orderLineId];
        return next;
      }
      const r0 = x.open[0] ?? 'COMMERCIAL';
      next[x.orderLineId] = {
        qty: 1,
        reason: r0,
        condition: r0 === 'WARRANTY' ? 'MOUNTED' : 'INTACT',
        warranty: {},
      };
      return next;
    });
  };
  const upd = (id: string, patch: Partial<Sel>) =>
    setSel((cur) => ({ ...cur, [id]: { ...cur[id], ...patch } }));

  const scanDocument = async () => {
    const v = docScan.trim();
    if (!v) return;
    try {
      const { data } = await apiClient.get<{ customerId: string; document: string }>(
        `/returns/find-document?doc=${encodeURIComponent(v)}`
      );
      const { data: c } = await apiClient.get<{
        id: string;
        name: string;
        company_name?: string | null;
        customer_type: string;
        code?: string | null;
      }>(`/customers/${data.customerId}`);
      setCustomer({
        id: c.id,
        name: c.company_name || c.name,
        code: c.code ?? null,
        customer_type: c.customer_type,
      });
      setSel({});
      setMode('DOC');
      setDoc(data.document);
    } catch (err) {
      showApiError(err);
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post<{ id: string; returnNumber: string; status: string }>(
        '/returns',
        {
          customerId: customerId ?? '00000000-0000-0000-0000-000000000000',
          lines: draftLines,
          partPresent: portal ? false : partPresent,
          pickup: portal ? pickup : 'COUNTER',
          comment: comment.trim() || null,
        },
        { headers: { 'X-Command-Id': cmd.current } }
      );
      push({
        tone: 'success',
        message: portal
          ? t(
              data.status === 'APPROVED'
                ? 'returnsV2.toast.portalApproved'
                : 'returnsV2.toast.portalSent',
              { number: data.returnNumber }
            )
          : t('returnsV2.toast.created', {
              number: data.returnNumber,
              status: t(`returnsV2.status.${data.status}`).toLowerCase(),
            }),
      });
      navigate(`${base}/${data.id}`);
    } catch (err) {
      push({ tone: 'error', message: returnError(t, err) });
      cmd.current = crypto.randomUUID();
    } finally {
      setBusy(false);
    }
  };

  if (portal && viewer) {
    return (
      <div>
        <PageHeader title={t('returnsV2.new.portalTitle')} />
        <div className="rounded-md bg-primary-soft px-3 py-2 text-sm text-primary">
          {t('returnsV2.new.viewerOnly')}
        </div>
      </div>
    );
  }

  const windowCell = (x: PurchaseView) => {
    if (x.window === 'OPEN')
      return (
        <>
          <Badge variant="success">
            {t('returnsV2.window.open', { until: formatDate(x.returnUntil) })}
          </Badge>
          {x.retractUntil && (
            <div className="text-xs text-muted-foreground">
              {t('returnsV2.window.retract', { until: formatDate(x.retractUntil) })}
            </div>
          )}
          <div className="text-xs text-muted-foreground">
            {t('returnsV2.window.warranty', { until: formatDate(x.warrantyUntil) })}
          </div>
        </>
      );
    if (x.window === 'WARRANTY')
      return (
        <>
          <Badge variant="warning">
            {t('returnsV2.window.over', { until: formatDate(x.returnUntil) })}
          </Badge>
          <div className="text-xs text-muted-foreground">
            {x.open.every((k) => k === 'CORE')
              ? t('returnsV2.window.coreOnly')
              : t('returnsV2.window.breakdownOnly', { until: formatDate(x.warrantyUntil) })}
          </div>
        </>
      );
    if (x.window === 'CLOSED')
      return (
        <>
          <Badge variant="destructive">{t('returnsV2.window.closed')}</Badge>
          <div className="text-xs text-muted-foreground">
            {t('returnsV2.window.closedSub', {
              until: formatDate(x.returnUntil),
              warranty: formatDate(x.warrantyUntil),
            })}
          </div>
        </>
      );
    return <Badge variant="muted">{t('returnsV2.window.done')}</Badge>;
  };

  const list = [
    ...Object.keys(sel)
      .map((id) => known[id])
      .filter(
        (x): x is PurchaseView =>
          !!x && !found.eligible.some((y) => y.orderLineId === x.orderLineId)
      ),
    ...found.eligible,
  ];
  const pvOf = (id: string) => preview?.lines.find((l) => l.orderLineId === id) ?? null;
  const blocking = !!preview?.lines.some((l) => l.level === 'BLOCKED');
  const study = !!preview?.needsStudy;
  const b2b = customer?.customer_type === 'B2B';

  return (
    <div className="app-tokens">
      {portal && <PortalReturnTabs />}
      <PageHeader
        title={portal ? t('returnsV2.new.portalTitle') : t('returnsV2.new.title')}
        description={
          portal
            ? t('returnsV2.new.portalLede', { days: policyDays ?? 5 })
            : t('returnsV2.new.lede', { days: policyDays ?? 5 })
        }
      />

      {!portal && (
        <Panel title={t('returnsV2.new.step1')} className="mb-3">
          <div className="flex flex-wrap items-end gap-3 px-4 py-3">
            <Field label={t('returnsV2.new.scanDoc')} className="min-w-[16rem] flex-1">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <ScanLine className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    className="pl-8 font-mono"
                    value={docScan}
                    onChange={(e) => setDocScan(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void scanDocument();
                    }}
                    placeholder="TCK-2026-…, BL-…, ORD-…"
                  />
                </div>
                <Button variant="outline" onClick={() => void scanDocument()}>
                  {t('returnsV2.new.find')}
                </Button>
              </div>
              <span className="mt-1 block text-xs normal-case tracking-normal text-muted-foreground">
                {t('returnsV2.new.scanDocHint')}
              </span>
            </Field>
            <Field label={t('returnsV2.new.orCustomer')} className="relative min-w-[16rem] flex-1">
              {customer ? (
                <div className="flex h-9 items-center justify-between rounded-md border px-3 text-sm">
                  <span>
                    <b>{customer.name}</b>
                    <span className="ml-2 text-xs text-muted-foreground">
                      {t(`returnsV2.customerType.${customer.customer_type}`)}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="text-xs text-primary hover:underline"
                    onClick={() => {
                      setCustomer(null);
                      setSel({});
                      setDoc('');
                      setMode('ART');
                      setCustQ('');
                    }}
                  >
                    {t('returnsV2.new.change')}
                  </button>
                </div>
              ) : (
                <>
                  <Input
                    value={custQ}
                    onChange={(e) => setCustQ(e.target.value)}
                    placeholder={t('returnsV2.new.customerHint')}
                  />
                  {custHits.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-lg">
                      {custHits.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className="block w-full px-3 py-2 text-left text-sm hover:bg-accent"
                          onClick={() => {
                            setCustomer(c);
                            setSel({});
                            setCustQ('');
                          }}
                        >
                          <b>{c.name}</b>
                          <span className="ml-2 text-xs text-muted-foreground">
                            {c.code ?? ''} · {t(`returnsV2.customerType.${c.customer_type}`)}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </Field>
            <Button variant="outline" onClick={() => setNoReceipt(true)}>
              {t('returnsV2.new.noReceipt')}
            </Button>
          </div>
        </Panel>
      )}

      {customer && (
        <>
          <Panel
            className="mb-3"
            title={
              portal
                ? t('returnsV2.new.whichPart')
                : t('returnsV2.new.step2', { name: customer.name })
            }
            actions={
              !portal ? (
                <span className="text-xs text-muted-foreground">
                  {t(`returnsV2.customerType.${customer.customer_type}`)}
                </span>
              ) : undefined
            }
          >
            <div className="flex flex-wrap items-end gap-3 border-b px-4 py-3">
              <div
                className="flex w-full flex-wrap gap-2"
                role="radiogroup"
                aria-label={t('returnsV2.new.searchBy')}
              >
                {(['ART', 'DOC'] as const).map((m) => (
                  <label
                    key={m}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm',
                      mode === m && 'border-primary bg-primary-soft'
                    )}
                  >
                    <input
                      type="radio"
                      name="mode"
                      checked={mode === m}
                      onChange={() => setMode(m)}
                    />
                    {t(`returnsV2.new.mode.${m}`)}
                  </label>
                ))}
              </div>
              {mode === 'DOC' ? (
                <Field label={t('returnsV2.new.docLabel')} className="min-w-[20rem] flex-1">
                  <Select value={doc} onChange={(e) => setDoc(e.target.value)}>
                    <option value="">{t('returnsV2.new.chooseDoc')}</option>
                    {docs.filter((b) => b.open).length > 0 && (
                      <optgroup label={t('returnsV2.new.docOpen')}>
                        {docs
                          .filter((b) => b.open)
                          .map((b) => (
                            <option key={b.document} value={b.document}>
                              {t('returnsV2.new.docOption', {
                                doc: b.document,
                                date: formatDate(b.date),
                                n: b.open,
                              })}
                            </option>
                          ))}
                      </optgroup>
                    )}
                    {docs.filter((b) => !b.open).length > 0 && (
                      <optgroup label={t('returnsV2.new.docWarranty')}>
                        {docs
                          .filter((b) => !b.open)
                          .map((b) => (
                            <option key={b.document} value={b.document}>
                              {t('returnsV2.new.docOptionW', {
                                doc: b.document,
                                date: formatDate(b.date),
                                n: b.warranty,
                              })}
                            </option>
                          ))}
                      </optgroup>
                    )}
                    {doc && !docs.some((b) => b.document === doc) && (
                      <option value={doc}>{t('returnsV2.new.docNone', { doc })}</option>
                    )}
                  </Select>
                </Field>
              ) : (
                <Field label={t('returnsV2.new.artLabel')} className="min-w-[20rem] flex-1">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      className="pl-8"
                      type="search"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder={t('returnsV2.new.artHint')}
                      autoComplete="off"
                    />
                  </div>
                </Field>
              )}
              <p className="w-full text-xs text-muted-foreground">
                {mode === 'DOC'
                  ? doc
                    ? t('returnsV2.new.hintDoc', { n: found.eligible.length, doc })
                    : t('returnsV2.new.hintDocs', { n: docs.length })
                  : q.trim()
                    ? t('returnsV2.new.hintQ', { n: found.eligible.length, q: q.trim() })
                    : t('returnsV2.new.hintDefault', {
                        n: found.eligible.length,
                        days: policyDays ?? 5,
                      })}
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className={cn(thCls, 'w-10')} />
                    <th className={thCls}>{t('returnsV2.new.col.part')}</th>
                    <th className={thCls}>{t('returnsV2.new.col.purchase')}</th>
                    <th className={cn(thCls, 'text-right')}>
                      {t(b2b ? 'returnsV2.new.col.priceHt' : 'returnsV2.new.col.priceTtc')}
                    </th>
                    <th className={cn(thCls, 'text-right')}>{t('returnsV2.new.col.qty')}</th>
                    <th className={thCls}>{t('returnsV2.new.col.return')}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.length === 0 && (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-10 text-center text-sm text-muted-foreground"
                      >
                        {mode === 'DOC'
                          ? doc
                            ? t('returnsV2.new.emptyDoc')
                            : t('returnsV2.new.emptyChooseDoc')
                          : q.trim()
                            ? found.other.length
                              ? t('returnsV2.new.emptyNotEligible')
                              : t('returnsV2.new.emptyNone')
                            : t('returnsV2.new.emptyRecent', { days: policyDays ?? 5 })}
                      </td>
                    </tr>
                  )}
                  {list.map((x) => {
                    const s = sel[x.orderLineId];
                    const pv = s ? pvOf(x.orderLineId) : null;
                    const reasons = x.open;
                    const max = s?.reason === 'CORE' ? x.returnableCore : x.returnableItem;
                    return [
                      <tr key={x.orderLineId} className={cn('border-b', s && 'bg-primary-soft/60')}>
                        <td className={tdCls}>
                          {(x.window === 'OPEN' || x.window === 'WARRANTY') && (
                            <input
                              type="checkbox"
                              className="h-5 w-5"
                              checked={!!s}
                              onChange={(e) => pick(x, e.target.checked)}
                              aria-label={t('returnsV2.new.pick', { sku: x.sku })}
                            />
                          )}
                        </td>
                        <td className={tdCls}>
                          <b className="font-mono text-xs">{x.sku}</b> {x.brand}
                          <div className="text-xs text-muted-foreground">
                            {x.name}
                            {x.nonReturnable ? ` · ${t('returnsV2.new.special')}` : ''}
                            {x.electrical ? ` · ${t('returnsV2.new.electrical')}` : ''}
                          </div>
                        </td>
                        <td className={tdCls}>
                          <span className="font-mono text-xs">{x.documents[0]}</span>
                          <div className="text-xs text-muted-foreground">
                            {t('returnsV2.new.deliveredOn', { date: formatDate(x.deliveredAt) })}
                            {x.payMethods.length
                              ? ` · ${x.payMethods.map((m) => t(`returnsV2.pay.${m}`, { defaultValue: m })).join(', ')}`
                              : ''}
                          </div>
                        </td>
                        <td className={cn(tdCls, 'text-right tabular-nums')}>
                          {b2b ? formatEUR(x.unitHt) : formatEUR(VAT(x.unitHt, x.vat))}
                        </td>
                        <td className={cn(tdCls, 'text-right tabular-nums')}>
                          {x.qty}
                          {x.returnedItem > 0 && (
                            <div className="text-xs text-muted-foreground">
                              {t('returnsV2.new.alreadyBack', { n: x.returnedItem })}
                            </div>
                          )}
                        </td>
                        <td className={tdCls}>{windowCell(x)}</td>
                      </tr>,
                      s && (
                        <tr key={`${x.orderLineId}-cfg`} className="border-b bg-muted/40">
                          <td />
                          <td colSpan={5} className="px-3 pb-3 pt-1">
                            <div className="grid gap-2 sm:grid-cols-5">
                              <Field label={t('returnsV2.new.qty')}>
                                <Select
                                  value={String(s.qty)}
                                  onChange={(e) =>
                                    upd(x.orderLineId, { qty: Number(e.target.value) })
                                  }
                                >
                                  {Array.from({ length: Math.max(1, max) }, (_, n) => (
                                    <option key={n} value={n + 1}>
                                      {n + 1}
                                    </option>
                                  ))}
                                </Select>
                              </Field>
                              <Field label={t('returnsV2.new.reason')} className="sm:col-span-2">
                                <Select
                                  value={reasons.includes(s.reason) ? s.reason : reasons[0]}
                                  onChange={(e) =>
                                    upd(x.orderLineId, {
                                      reason: e.target.value,
                                      qty: 1,
                                      condition:
                                        e.target.value === 'WARRANTY' ? 'MOUNTED' : s.condition,
                                    })
                                  }
                                >
                                  {reasons.map((k) => (
                                    <option key={k} value={k}>
                                      {reasonLabel(t, k)}
                                    </option>
                                  ))}
                                </Select>
                              </Field>
                              {s.reason !== 'CORE' ? (
                                <Field
                                  label={t('returnsV2.new.condition')}
                                  className="sm:col-span-2"
                                >
                                  <Select
                                    value={s.condition}
                                    onChange={(e) =>
                                      upd(x.orderLineId, { condition: e.target.value })
                                    }
                                  >
                                    {CONDITIONS.map((k) => (
                                      <option key={k} value={k}>
                                        {condLabel(t, k)}
                                      </option>
                                    ))}
                                  </Select>
                                </Field>
                              ) : (
                                <div className="text-xs text-muted-foreground sm:col-span-2 sm:pt-6">
                                  {t('returnsV2.new.coreHint')}
                                </div>
                              )}
                            </div>
                            {s.reason === 'WARRANTY' && (
                              <div className="mt-2 grid gap-2 sm:grid-cols-5">
                                <Field label={t('returnsV2.new.plate')}>
                                  <Input
                                    value={s.warranty.plate ?? ''}
                                    onChange={(e) =>
                                      upd(x.orderLineId, {
                                        warranty: { ...s.warranty, plate: e.target.value },
                                      })
                                    }
                                    placeholder="AB-123-CD"
                                  />
                                </Field>
                                <Field label={t('returnsV2.new.km')}>
                                  <Input
                                    inputMode="numeric"
                                    value={s.warranty.km ?? ''}
                                    onChange={(e) =>
                                      upd(x.orderLineId, {
                                        warranty: { ...s.warranty, km: e.target.value },
                                      })
                                    }
                                  />
                                </Field>
                                <Field label={t('returnsV2.new.mountedAt')}>
                                  <Input
                                    type="date"
                                    value={s.warranty.mountedAt ?? ''}
                                    onChange={(e) =>
                                      upd(x.orderLineId, {
                                        warranty: { ...s.warranty, mountedAt: e.target.value },
                                      })
                                    }
                                  />
                                </Field>
                                <Field
                                  label={t('returnsV2.new.description')}
                                  className="sm:col-span-2"
                                >
                                  <Input
                                    value={s.warranty.description ?? ''}
                                    onChange={(e) =>
                                      upd(x.orderLineId, {
                                        warranty: { ...s.warranty, description: e.target.value },
                                      })
                                    }
                                    placeholder={t('returnsV2.new.descriptionHint')}
                                  />
                                </Field>
                              </div>
                            )}
                            {pv && (
                              <Verdict
                                level={pv.level}
                                msgs={pv.msgs}
                                legal={pv.legal}
                                incomplete={pv.incomplete}
                                amount={pv.estimateTtc}
                                detail={
                                  pv.feeHt > 0
                                    ? t('returnsV2.new.feeDetail', {
                                        item: formatEUR(VAT(pv.itemHt, x.vat)),
                                        pct: pv.feePct,
                                        fee: formatEUR(VAT(pv.feeHt, x.vat)),
                                      })
                                    : null
                                }
                              />
                            )}
                          </td>
                        </tr>
                      ),
                    ];
                  })}
                </tbody>
              </table>
            </div>
            {found.other.length > 0 && (
              <div className="border-t px-4 py-2 text-xs text-muted-foreground">
                <b>{t('returnsV2.new.notEligible')}</b>{' '}
                {found.other.map((x) => (
                  <span key={x.orderLineId} className="mr-2">
                    <span className="font-mono">{x.sku}</span> ({x.documents[0]},{' '}
                    {x.window === 'DONE'
                      ? t('returnsV2.window.done').toLowerCase()
                      : t('returnsV2.new.notEligibleWhy', {
                          until: formatDate(x.returnUntil),
                          warranty: formatDate(x.warrantyUntil),
                        })}
                    )
                  </span>
                ))}
              </div>
            )}
          </Panel>

          <Panel title={portal ? t('returnsV2.new.send') : t('returnsV2.new.step3')}>
            <div className="grid gap-3 px-4 py-3">
              {portal ? (
                <div role="radiogroup" aria-label={t('returnsV2.new.partComes')}>
                  <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    {t('returnsV2.new.partComes')}
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {(['COUNTER', 'DRIVER'] as const).map((p) => (
                      <label
                        key={p}
                        className={cn(
                          'flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm normal-case tracking-normal text-foreground',
                          pickup === p && 'border-primary bg-primary-soft'
                        )}
                      >
                        <input
                          type="radio"
                          name="pickup"
                          checked={pickup === p}
                          onChange={() => setPickup(p)}
                        />
                        {t(`returnsV2.new.pickup.${p}`)}
                      </label>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium">
                    <input
                      type="checkbox"
                      className="h-5 w-5"
                      checked={partPresent}
                      onChange={(e) => setPartPresent(e.target.checked)}
                    />
                    {t('returnsV2.new.present')}
                  </label>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('returnsV2.new.presentHint')}
                  </p>
                </div>
              )}
              <Field label={portal ? t('returnsV2.new.commentPortal') : t('returnsV2.new.comment')}>
                <Input
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={t('returnsV2.new.optional')}
                />
              </Field>
              <div className="flex flex-wrap gap-6 text-sm">
                <span>
                  {t('returnsV2.new.chosen')} <b>{draftLines.length}</b>
                </span>
                <span>
                  {study ? t('returnsV2.new.estimateAfter') : t('returnsV2.new.estimate')}{' '}
                  <b className="tabular-nums">{formatEUR(preview?.totalTtc ?? 0)}</b>
                </span>
              </div>
              {preview?.errors.map((e) => (
                <div
                  key={e}
                  className="rounded-md bg-destructive/[.12] px-3 py-2 text-sm font-medium text-destructive"
                >
                  {t(`returnsV2.previewError.${e}`, { defaultValue: e })}
                </div>
              ))}
              {blocking && (
                <div className="rounded-md bg-destructive/[.12] px-3 py-2 text-sm font-medium text-destructive">
                  {t('returnsV2.new.blocking')}
                </div>
              )}
              {study && !blocking && (
                <div className="rounded-md bg-warning/[.13] px-3 py-2 text-sm font-medium text-warning">
                  {portal ? t('returnsV2.new.studyPortal') : t('returnsV2.new.study')}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  disabled={
                    busy || !draftLines.length || blocking || !!preview?.errors.length || !preview
                  }
                  onClick={() => void submit()}
                >
                  {portal ? t('returnsV2.new.sendBtn') : t('returnsV2.new.save')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSel({});
                    setComment('');
                    setQ('');
                    setDoc('');
                  }}
                >
                  {t('returnsV2.new.reset')}
                </Button>
              </div>
            </div>
          </Panel>
        </>
      )}

      {noReceipt && (
        <NoReceiptDialog
          onClose={() => setNoReceipt(false)}
          onDone={(id) => navigate(`/returns/${id}`)}
        />
      )}
    </div>
  );
}

export { cmdHeaders };
